<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1><font-awesome-icon icon="comments"/> AI Agent — Lịch sử hội thoại</h1>
        <p>Hội thoại bot trả lời tin nhắn khách. Click để xem flow chi tiết.</p>
      </div>
    </div>

    <div class="row">
      <!-- Tabs trạng thái -->
      <div class="col-12">
        <b-tabs v-model="statusTabIndex" @input="onStatusTab">
          <b-tab title="Tất cả"></b-tab>
          <b-tab>
            <template #title>
              <span class="log-dot" style="background: #28a745"></span> Đã gửi
            </template>
          </b-tab>
          <b-tab>
            <template #title>
              <span class="log-dot" style="background: #dc3545"></span> Lỗi
            </template>
          </b-tab>
          <b-tab>
            <template #title>
              <span class="log-dot" style="background: #ffc107"></span> Đang chờ
            </template>
          </b-tab>
          <b-tab>
            <template #title>
              <span class="log-dot" style="background: #fd7e14"></span> Tool lỗi
            </template>
          </b-tab>
        </b-tabs>
      </div>

      <!-- Filter -->
      <div class="col-12">
        <div class="tile">
          <div class="form-inline">
            <b-form-input class="mr-1 mb-1" v-model="filter.keyword" v-on:keydown.enter="load"
                          placeholder="Tìm nội dung, lỗi..." style="min-width: 260px"></b-form-input>
            <b-form-input type="date" class="mr-1 mb-1" v-model="filter.from" style="width: 160px"></b-form-input>
            <span class="mx-1 mb-1">→</span>
            <b-form-input type="date" class="mr-1 mb-1" v-model="filter.to" style="width: 160px"></b-form-input>
            <button @click="load" class="btn btn-primary mr-1 mb-1">
              <font-awesome-icon icon="search"/> Tìm kiếm
            </button>
            <button @click="reset" class="btn btn-secondary mr-1 mb-1">Reset</button>
            <button @click="exportCSV" class="btn btn-success ml-auto mb-1" :disabled="exporting">
              <font-awesome-icon icon="download"/> CSV
            </button>
          </div>
        </div>
      </div>

      <!-- Paging top -->
      <div class="col-12">
        <div class="c-paging clearfix">
          <div class="c-paging__count" v-if="totalDocs">Tổng {{ totalDocs }} kết quả</div>
          <div class="c-paging__right">
            <b-pagination v-if="totalDocs > pageSize" class="pull-right" size="sm"
                          v-model="page" :total-rows="totalDocs" :per-page="pageSize"
                          @change="onPageChange"></b-pagination>
          </div>
        </div>
      </div>

      <!-- Card list -->
      <div class="col-12" v-if="items.length">
        <div class="log-list">
          <div v-for="item in items" :key="item._id"
               class="log-card" :class="rowClass(item)"
               :style="{ borderLeftColor: PLATFORM_COLORS[platformKey(item)] || '#888' }"
               @click="showDetail(item)" role="button">
            <div class="log-card__time">
              <div class="log-time-main">{{ item.createTime | moment('DD/MM HH:mm') }}</div>
              <div class="log-meta">{{ item.timing && item.timing.total ? (item.timing.total / 1000).toFixed(1) + 's' : '—' }}</div>
              <div class="log-meta" v-if="item.tokenUsage">{{ item.tokenUsage.totalTokens }} tok</div>
            </div>

            <div class="log-card__platform">
              <span class="log-channel-pill"
                    :style="{ background: PLATFORM_COLORS[platformKey(item)] || '#888' }">
                {{ platformLabel(item) }}
              </span>
              <div class="log-meta mt-1" v-if="item.bot">{{ item.bot.name }}</div>
            </div>

            <div class="log-card__main">
              <div class="log-row-1">
                <span v-if="item.client" class="log-client mr-3">
                  <font-awesome-icon icon="building" class="text-muted mr-1"/>
                  {{ item.client.name }}
                </span>
                <span v-if="item.customer" class="log-user">
                  <font-awesome-icon icon="user" class="text-muted mr-1"/>
                  {{ item.customer.name || item.customer.phone || '—' }}
                </span>
              </div>
              <div class="log-row-2 log-question" v-if="item.message && item.message.content">
                <font-awesome-icon icon="quote-left" class="text-muted mr-1"/>
                {{ truncate(item.message.content, 120) }}
              </div>
              <div class="log-row-2 log-answer" v-if="item.content">
                <font-awesome-icon icon="robot" class="text-success mr-1"/>
                {{ truncate(item.content, 120) }}
              </div>
              <div class="log-row-3" v-if="item.errors && item.errors.length">
                <b-badge variant="warning" class="mr-1"
                         :title="item.errors.map(e => '[' + e.tool + '] ' + e.message).join('\n')">
                  {{ item.errors.length }} tool lỗi
                </b-badge>
              </div>
              <div class="log-row-3 text-danger" v-if="item.errorMsg">
                <font-awesome-icon icon="exclamation-triangle" class="mr-1"/>
                {{ truncate(item.errorMsg, 120) }}
              </div>
            </div>

            <div class="log-card__status">
              <b-badge v-if="item.error" variant="danger">Lỗi</b-badge>
              <b-badge v-else-if="item.sendTime > 0" variant="success">Đã gửi</b-badge>
              <b-badge v-else variant="warning">Chờ</b-badge>
            </div>

            <div class="log-card__chev"><font-awesome-icon icon="chevron-right"/></div>
          </div>
        </div>
      </div>

      <div class="col-12 text-center text-muted py-4" v-if="!items.length">
        <font-awesome-icon icon="inbox" size="3x" class="mb-2 d-block"/>
        Không có dữ liệu
      </div>

      <!-- Paging bottom -->
      <div class="col-12" v-if="totalDocs > pageSize">
        <div class="c-paging clearfix">
          <div class="c-paging__count">Tổng {{ totalDocs }} kết quả</div>
          <div class="c-paging__right">
            <b-pagination class="pull-right" size="sm"
                          v-model="page" :total-rows="totalDocs" :per-page="pageSize"
                          @change="onPageChange"></b-pagination>
          </div>
        </div>
      </div>
    </div>

    <!-- Detail modal -->
    <b-modal v-model="showModal" size="xl" hide-footer scrollable
             :modal-class="['log-modal']" body-class="log-modal-body">
      <template #modal-title>
        <span v-if="detail">
          <span class="log-channel-pill" :style="{ background: PLATFORM_COLORS[platformKey(detail)] || '#888' }">
            {{ platformLabel(detail) }}
          </span>
          <span class="ml-2">Chi tiết hội thoại</span>
          <span class="ml-2 small text-muted">{{ detail.createTime | moment('DD/MM/YYYY HH:mm:ss') }}</span>
        </span>
      </template>

      <div v-if="detail">
        <div class="log-meta-grid">
          <div class="log-meta-cell">
            <div class="log-meta-label">Trạng thái</div>
            <span v-if="detail.error" class="badge badge-danger">Lỗi</span>
            <span v-else-if="detail.sendTime > 0" class="badge badge-success">Đã gửi</span>
            <span v-else class="badge badge-warning">Đang chờ</span>
          </div>
          <div class="log-meta-cell">
            <div class="log-meta-label">Xử lý</div>
            <div>
              <span v-if="detail.timing && detail.timing.total">{{ (detail.timing.total / 1000).toFixed(1) }}s</span>
              <span v-else-if="!detail.sendTime && !detail.error" class="text-warning">
                Đang xử lý ({{ Math.round((Date.now() - detail.createTime) / 1000) }}s)
              </span>
              <span v-else>—</span>
            </div>
          </div>
          <div class="log-meta-cell" v-if="detail.tokenUsage && detail.tokenUsage.totalTokens">
            <div class="log-meta-label">Token</div>
            <div>{{ detail.tokenUsage.totalTokens }}
              <span class="log-meta">(in={{ detail.tokenUsage.promptTokens }}, out={{ detail.tokenUsage.completionTokens }})</span>
            </div>
          </div>
          <div class="log-meta-cell">
            <div class="log-meta-label">Bot</div>
            <div>{{ detail.bot ? detail.bot.name : '—' }}</div>
          </div>
        </div>
        <div class="log-meta-grid mt-2">
          <div class="log-meta-cell log-meta-cell--wide">
            <div class="log-meta-label">Khách hàng</div>
            <div>
              {{ detail.client ? detail.client.name : '—' }}
              <span class="log-meta" v-if="detail.client && detail.client.domain">({{ detail.client.domain }})</span>
            </div>
          </div>
          <div class="log-meta-cell">
            <div class="log-meta-label">Khách</div>
            <div>{{ detail.customer ? (detail.customer.name || detail.customer.phone || '—') : '—' }}</div>
          </div>
        </div>

        <div v-if="detail.errorMsg" class="alert alert-danger mt-3">
          <strong>Lỗi:</strong> {{ detail.errorMsg }}
        </div>

        <div v-if="detail.errors && detail.errors.length" class="alert alert-warning mt-3">
          <strong>Tool errors ({{ detail.errors.length }}):</strong>
          <div v-for="(err, i) in detail.errors" :key="'err' + i" class="mt-1">
            <b-badge variant="warning">{{ err.tool }}</b-badge> {{ err.message }}
          </div>
        </div>

        <div class="log-section">
          <div class="log-section__label log-section__label--big">
            <font-awesome-icon icon="quote-left" class="text-muted mr-2"/> Khách hỏi
          </div>
          <div class="log-content-block">{{ detail.message ? detail.message.content : '—' }}<b-badge
            v-if="detail.message && detail.message.dataType && detail.message.dataType !== 'text'"
            variant="info" class="ml-1">{{ detail.message.dataType }}</b-badge></div>
        </div>

        <div class="log-section" v-if="detail.content">
          <div class="log-section__label log-section__label--big">
            <font-awesome-icon icon="robot" class="text-success mr-2"/> Bot trả lời
          </div>
          <div class="log-content-block">{{ detail.content }}</div>
        </div>

        <div class="log-section" v-if="detail.steps && detail.steps.length">
          <div class="log-section__label log-section__label--big">
            <font-awesome-icon icon="project-diagram" class="text-info mr-2"/>
            Quá trình xử lý ({{ detail.steps.length }} steps)
          </div>
          <div class="step-flow">
            <div v-for="(step, i) in detail.steps" :key="'s' + i" class="step-item">
              <div v-if="step.type === 'HumanMessage'" class="d-flex">
                <b-badge variant="secondary" class="step-badge">{{ step.step }}</b-badge>
                <div class="flex-fill">
                  <b-badge variant="light" class="border">HumanMessage</b-badge>
                  <div class="step-content mt-1" :class="{'step-collapsed': !expandedSteps[i]}"
                       @click="$set(expandedSteps, i, !expandedSteps[i])">
                    {{ typeof step.content === 'string' ? step.content : JSON.stringify(step.content, null, 2) }}
                  </div>
                </div>
              </div>

              <div v-else-if="step.type === 'AIMessage'" class="d-flex">
                <b-badge variant="success" class="step-badge text-white">{{ step.step }}</b-badge>
                <div class="flex-fill">
                  <b-badge variant="success">AIMessage</b-badge>
                  <b-badge v-for="tc in (step.tool_calls || [])" :key="tc.name + tc.toolIndex" variant="info" class="ml-1">
                    {{ tc.name }}
                  </b-badge>
                  <div v-if="step.content" class="step-content mt-1" :class="{'step-collapsed': !expandedSteps[i]}"
                       @click="$set(expandedSteps, i, !expandedSteps[i])"
                       style="white-space: pre-wrap;">{{ step.content }}</div>
                  <div v-if="step.tool_calls && step.tool_calls.length" class="mt-1">
                    <div v-for="tc in step.tool_calls" :key="tc.name + tc.toolIndex" class="ml-3 mb-1">
                      <small class="text-info cursor-pointer"
                             @click="$set(expandedSteps, 'tc_' + i + '_' + tc.toolIndex, !expandedSteps['tc_' + i + '_' + tc.toolIndex])">
                        <font-awesome-icon
                          :icon="expandedSteps['tc_' + i + '_' + tc.toolIndex] ? 'chevron-down' : 'chevron-right'"
                          class="mr-1"/>
                        <strong>{{ tc.name }}</strong>({{ tc.args ? Object.keys(tc.args).join(', ') : '' }})
                      </small>
                      <pre v-if="expandedSteps['tc_' + i + '_' + tc.toolIndex]" class="step-pre">{{ JSON.stringify(tc.args, null, 2) }}</pre>
                    </div>
                  </div>
                </div>
              </div>

              <div v-else-if="step.type === 'ToolMessage'" class="d-flex">
                <b-badge :variant="isToolError(step) ? 'warning' : 'info'" class="step-badge text-white">{{ step.step }}</b-badge>
                <div class="flex-fill">
                  <b-badge :variant="isToolError(step) ? 'warning' : 'info'">ToolMessage</b-badge>
                  <small class="text-muted ml-1" v-if="step.tool_call_id">{{ step.tool_call_id }}</small>
                  <div class="step-content mt-1 cursor-pointer" :class="{'step-collapsed': !expandedSteps[i]}"
                       @click="$set(expandedSteps, i, !expandedSteps[i])">
                    <pre v-if="isJsonContent(step.content)" class="step-pre"
                         :class="isToolError(step) ? 'step-pre--warn' : ''">{{ formatJson(step.content) }}</pre>
                    <div v-else class="text-muted" style="white-space: pre-wrap;">{{ step.content }}</div>
                  </div>
                </div>
              </div>

              <div v-else class="d-flex">
                <b-badge variant="secondary" class="step-badge">{{ step.step }}</b-badge>
                <div class="flex-fill">
                  <b-badge variant="secondary">{{ step.type }}</b-badge>
                  <div class="step-content mt-1" :class="{'step-collapsed': !expandedSteps[i]}"
                       @click="$set(expandedSteps, i, !expandedSteps[i])">
                    {{ typeof step.content === 'string' ? step.content : JSON.stringify(step.content, null, 2) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="!detail.sendTime && !detail.error" class="text-center text-muted py-4">
          <font-awesome-icon icon="spinner" spin class="mr-1"/> AI đang xử lý, chưa có steps...
          <div class="mt-2">
            <b-button size="sm" variant="outline-primary" @click="refreshDetail">Tải lại</b-button>
          </div>
        </div>
      </div>
    </b-modal>
  </main>
</template>

<script>
const PLATFORM_COLORS = {
  facebook: '#1877F2',
  instagram: '#E1306C',
  zalo: '#0068FF',
  telegram: '#0088CC',
  whatsapp: '#25D366',
  livechat: '#6c757d',
  unknown: '#888'
};

export default {
  data() {
    var now = new Date();
    var sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return {
      items: [],
      page: 1,
      pageSize: 20,
      totalDocs: 0,
      statusTabIndex: 0,
      filter: {
        hasError: '',
        hasToolError: '',
        keyword: '',
        from: sevenDaysAgo.toISOString().split('T')[0],
        to: now.toISOString().split('T')[0],
      },
      PLATFORM_COLORS,
      showModal: false,
      detail: null,
      expandedSteps: {},
      exporting: false,
    };
  },
  methods: {
    load() {
      var params = { page: this.page, pageSize: this.pageSize };
      if (this.filter.hasError) params.hasError = this.filter.hasError;
      if (this.filter.hasToolError) params.hasToolError = this.filter.hasToolError;
      if (this.filter.keyword) params.keyword = this.filter.keyword;
      if (this.filter.from) params.from = new Date(this.filter.from).getTime();
      if (this.filter.to) params.to = new Date(this.filter.to).getTime() + 24 * 60 * 60 * 1000 - 1;

      this.$service.get('/ai-agent/logs', { params: params }).then(res => {
        this.items = res.data.docs;
        this.totalDocs = res.data.totalDocs;
      });
    },
    onPageChange(page) {
      this.page = page;
      this.load();
    },
    onStatusTab(idx) {
      // 0=Tất cả 1=Đã gửi 2=Lỗi 3=Chờ 4=Tool lỗi
      this.filter.hasError = '';
      this.filter.hasToolError = '';
      if (idx === 1) this.filter.hasError = 'false';
      else if (idx === 2) this.filter.hasError = 'true';
      else if (idx === 3) this.filter.hasError = 'pending';
      else if (idx === 4) this.filter.hasToolError = 'true';
      this.page = 1;
      this.load();
    },
    showDetail(item) {
      this.expandedSteps = {};
      this.$service.get('/ai-agent/logs/' + item._id).then(res => {
        this.detail = res.data;
        this.showModal = true;
      });
    },
    refreshDetail() {
      if (!this.detail) return;
      this.$service.get('/ai-agent/logs/' + this.detail._id).then(res => {
        this.detail = res.data;
      });
    },
    reset() {
      var now = new Date();
      var sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      this.filter = {
        hasError: '', hasToolError: '', keyword: '',
        from: sevenDaysAgo.toISOString().split('T')[0],
        to: now.toISOString().split('T')[0],
      };
      this.statusTabIndex = 0;
      this.page = 1;
      this.load();
    },
    isToolError(step) {
      if (!step.content) return false;
      try {
        var result = JSON.parse(step.content);
        return result && result.success === false;
      } catch (e) { return false; }
    },
    isJsonContent(content) {
      if (!content) return false;
      try { JSON.parse(content); return true; } catch (e) { return false; }
    },
    formatJson(content) {
      try { return JSON.stringify(JSON.parse(content), null, 2); } catch (e) { return content; }
    },
    exportCSV() {
      this.exporting = true;
      var params = {};
      if (this.filter.hasError) params.hasError = this.filter.hasError;
      if (this.filter.keyword) params.keyword = this.filter.keyword;
      if (this.filter.from) params.from = new Date(this.filter.from).getTime();
      if (this.filter.to) params.to = new Date(this.filter.to).getTime() + 24 * 60 * 60 * 1000 - 1;

      this.$service.get('/ai-agent/export', { params: params, responseType: 'blob' })
        .then(res => {
          var url = window.URL.createObjectURL(new Blob([res.data]));
          var a = document.createElement('a');
          a.href = url;
          a.download = 'ai-agent-logs.csv';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        })
        .finally(() => { this.exporting = false; });
    },
    truncate(str, len) {
      if (!str) return '—';
      return str.length > len ? str.substring(0, len) + '...' : str;
    },
    platformKey(item) {
      if (item.account && item.account.type) return item.account.type;
      if (item.livechat) return 'livechat';
      return 'unknown';
    },
    platformLabel(item) {
      var labels = { facebook: 'Facebook', instagram: 'Instagram', zalo: 'Zalo', telegram: 'Telegram', whatsapp: 'WhatsApp', livechat: 'LiveChat' };
      return labels[this.platformKey(item)] || '—';
    },
    rowClass(item) {
      if (item.error) return 'log-card--error';
      if (item.errors && item.errors.length) return 'log-card--warn';
      if (item.sendTime > 0) return 'log-card--success';
      return 'log-card--pending';
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.log-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
}

.log-list { display: flex; flex-direction: column; gap: 8px; }

.log-card {
  display: flex;
  align-items: stretch;
  background: #fff;
  border: 1px solid #e8eaed;
  border-left: 4px solid #888;
  border-radius: 6px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.15s;
  gap: 16px;
}
.log-card:hover { border-color: #c9d1d9; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06); transform: translateY(-1px); }
.log-card--error { background: #fef6f6; }
.log-card--warn { background: #fffaf3; }

.log-card__time { min-width: 100px; flex-shrink: 0; }
.log-time-main { font-size: 13px; color: #333; font-weight: 500; }
.log-meta { font-size: 11px; color: #888; }

.log-card__platform { min-width: 90px; flex-shrink: 0; display: flex; flex-direction: column; align-items: flex-start; }
.log-channel-pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.log-card__main { flex: 1; display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.log-row-1 { display: flex; align-items: center; flex-wrap: wrap; }
.log-row-2 { font-size: 13px; color: #444; }
.log-question, .log-answer {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.log-row-3 { font-size: 12px; }
.log-client, .log-user { font-size: 13px; color: #555; }

.log-card__status { display: flex; align-items: center; flex-shrink: 0; }
.log-card__chev { display: flex; align-items: center; color: #bbb; flex-shrink: 0; }

/* Modal */
.log-meta-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
}
.log-meta-cell--wide { grid-column: span 3; }
.log-meta-label { font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }

.log-section { margin-top: 20px; }
.log-section__label { font-size: 12px; color: #888; text-transform: uppercase; margin-bottom: 6px; }
.log-section__label--big {
  font-size: 14px; color: #222; text-transform: none; font-weight: 600;
  padding-bottom: 6px; border-bottom: 1px solid #e8eaed;
}
.log-content-block {
  background: #f6f8fa; padding: 12px; border-radius: 4px;
  white-space: pre-wrap; word-break: break-word; font-size: 14px; line-height: 1.6;
}

.step-flow { display: flex; flex-direction: column; gap: 12px; }
.step-item {}
.step-badge { min-width: 36px; align-self: flex-start; margin-right: 8px; }
.step-content { cursor: pointer; }
.step-collapsed {
  max-height: 60px; overflow: hidden; position: relative;
}
.step-collapsed::after {
  content: '... (click để xem thêm)';
  position: absolute; bottom: 0; right: 0;
  background: linear-gradient(to right, transparent, white 40%);
  padding-left: 40px; font-size: 11px; color: #007bff;
}
.step-pre {
  background: #f6f8fa; padding: 8px 12px; border-radius: 4px;
  font-size: 12px; max-height: 300px; overflow: auto;
  white-space: pre-wrap; margin: 4px 0 0 0;
}
.step-pre--warn { background: #fff3cd; }
.cursor-pointer { cursor: pointer; }
</style>
<style>
.log-modal .modal-xl { max-width: 1200px; }
</style>
